// SCR-STAFF-905 — جادوی ۵ مرحله‌ای ورود داده (US-PLT-060 تا 063؛ AP-PLT-03): نوع ← بارگذاری ← نگاشت (ستون فایل ↔ فیلد ققنوس،
// پیش‌نمایش ۱۰ ردیف نرمال‌شده، واحد پول، قالب تاریخ، پروفایل) ← اجرای آزمایشی (کارت‌ها، جدول خطا، فایل خطا) ← اجرا (پیشرفت زنده،
// رد ردیف‌های نامعتبر) و نتیجه با بازگشت (غیرفعال با توضیح وابستگی‌ها).
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type ImportDateFormat,
  type ImportFieldView,
  type ImportJobView,
  type ImportMappingView,
  type ImportMoneyUnit,
  type ImportRowErrorView,
  type ImportTypeView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, LoadingState, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { RollbackAction } from './RollbackAction';
import {
  EXECUTED_STATUSES,
  ImportStatusChip,
  importKeys,
  isBusy,
  notValidCount,
  saveBlob,
  useImportChanged,
} from './common';

const STEPS = ['type', 'upload', 'mapping', 'dry_run', 'execute'] as const;

/** مرحله متناظر وضعیت کار (ادامه از تاریخچه). */
export function stepOf(job: ImportJobView): number {
  switch (job.status) {
    case 'Uploaded':
    case 'Mapped':
      return 2;
    case 'Validating':
    case 'Validated':
    case 'ValidationFailed':
      return 3;
    default:
      return 4;
  }
}

export interface ImportWizardProps {
  /** کار انتخاب‌شده از تاریخچه. */
  jobId?: string | null;
  onJobChange: (jobId: string | null) => void;
  onShowHistory: () => void;
}

export function ImportWizard({ jobId = null, onJobChange, onShowHistory }: ImportWizardProps) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [type, setType] = useState<string | null>(null);

  const job = useQuery({
    queryKey: importKeys.job(session?.tenantId, jobId ?? ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/imports/{id}', { params: { path: { id: jobId ?? '' } } })),
    enabled: jobId !== null,
    refetchInterval: (query) => (isBusy(query.state.data) ? 1000 : false),
  });

  // پیشرفت زنده (Hub PLT) کنار خواندن دوره‌ای.
  useImportChanged(isBusy(job.data), (message) => {
    if (message.jobId === jobId) {
      void queryClient.invalidateQueries({ queryKey: importKeys.job(session?.tenantId, message.jobId) });
    }
  });

  // کار انتخاب‌شده از تاریخچه: مرحله از وضعیت.
  const loadedId = job.data?.id;
  useEffect(() => {
    if (job.data && loadedId === jobId) {
      setType(job.data.type);
      setStep((current) => (current < 2 ? stepOf(job.data) : current));
    }
    // فقط هنگام تغییر کار.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId, jobId]);

  const refreshJob = (updated: ImportJobView) => {
    queryClient.setQueryData(importKeys.job(session?.tenantId, updated.id), updated);
    void queryClient.invalidateQueries({ queryKey: importKeys.list(session?.tenantId) });
  };

  const restart = () => {
    onJobChange(null);
    setType(null);
    setStep(0);
  };

  return (
    <Stack spacing={2} data-testid="import-wizard">
      <Stepper activeStep={step} alternativeLabel>
        {STEPS.map((key) => (
          <Step key={key}>
            <StepLabel>{t(`plt:import.steps.${key}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 0 && (
        <TypeStep
          selected={type}
          onSelect={setType}
          onNext={() => {
            onJobChange(null);
            setStep(1);
          }}
        />
      )}
      {step === 1 && type && (
        <UploadStep
          type={type}
          onBack={() => setStep(0)}
          onUploaded={(uploaded) => {
            refreshJob(uploaded);
            onJobChange(uploaded.id);
            setStep(2);
          }}
        />
      )}
      {step >= 2 && jobId && !job.data && (job.isPending ? <LoadingState /> : <ErrorState error={job.error} onRetry={() => void job.refetch()} />)}
      {step === 2 && job.data && (
        <MappingStep
          job={job.data}
          onBack={() => setStep(1)}
          onValidating={(updated) => {
            refreshJob(updated);
            setStep(3);
          }}
        />
      )}
      {step === 3 && job.data && (
        <DryRunStep job={job.data} onEditMapping={() => setStep(2)} onNext={() => setStep(4)} />
      )}
      {step === 4 && job.data && (
        <ExecuteStep job={job.data} onUpdated={refreshJob} onRestart={restart} onShowHistory={onShowHistory} />
      )}
    </Stack>
  );
}

function TypeStep({
  selected,
  onSelect,
  onNext,
}: {
  selected: string | null;
  onSelect: (type: string) => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const labelId = useId();
  const types = useQuery({
    queryKey: importKeys.types(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/imports/types')),
    staleTime: 5 * 60_000,
  });
  const [downloadError, setDownloadError] = useState<unknown>();

  const download = async (item: ImportTypeView) => {
    setDownloadError(undefined);
    const result = await tenantApi.GET('/api/v1/platform/imports/templates/{type}', {
      params: { path: { type: item.type } },
      parseAs: 'blob',
    });
    if (result.response.ok && result.data) {
      saveBlob(result.data as Blob, `${item.type}.xlsx`);
    } else {
      setDownloadError(result.error ?? new Error('download'));
    }
  };

  if (types.isPending) {
    return <LoadingState rows={4} />;
  }
  if (types.error) {
    return <ErrorState error={types.error} onRetry={() => void types.refetch()} />;
  }

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{t('plt:import.type_step.hint')}</Typography>
      <FormControl>
        <FormLabel id={labelId}>{t('plt:import.steps.type')}</FormLabel>
        <RadioGroup aria-labelledby={labelId} value={selected ?? ''} onChange={(event) => onSelect(event.target.value)}>
          {types.data.items.map((item) => (
            <Stack key={item.type} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }} data-testid={`import-type-${item.type}`}>
              <FormControlLabel
                value={item.type}
                disabled={!item.available}
                control={<Radio />}
                label={t(`plt:import.types.${item.type}`)}
              />
              {!item.available && <Chip size="small" label={t('plt:import.type_step.later')} />}
              {item.dependsOn.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('plt:import.type_step.depends_on', {
                    types: item.dependsOn.map((dependency) => t(`plt:import.types.${dependency}`)).join(t('plt:import.list_separator')),
                  })}
                </Typography>
              )}
              {item.templateAvailable && (
                <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => void download(item)} data-testid={`import-template-${item.type}`}>
                  {t('plt:import.type_step.template')}
                </Button>
              )}
            </Stack>
          ))}
        </RadioGroup>
      </FormControl>
      {downloadError ? <ErrorState error={downloadError} compact /> : null}
      <Box>
        <Button variant="contained" disabled={!selected} onClick={onNext} data-testid="import-type-next">
          {t('plt:import.type_step.next')}
        </Button>
      </Box>
    </Stack>
  );
}

function UploadStep({
  type,
  onBack,
  onUploaded,
}: {
  type: string;
  onBack: () => void;
  onUploaded: (job: ImportJobView) => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('plt.import.run');
  const idempotency = useIdempotencyKey();
  const inputId = useId();
  const [file, setFile] = useState<File | null>(null);
  // AP-IAM-05 (US-PLT-065): نوع بایگانی (Documents) فقط zip با manifest.csv می‌پذیرد.
  const types = useQuery({
    queryKey: importKeys.types(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/imports/types')),
    staleTime: 5 * 60_000,
  });
  const archive = types.data?.items.find((item) => item.type === type)?.archive === true;
  // AP-PLT-07 (US-PLT-064): مبدل سامانه قبلی اختیاری برای این نوع.
  const [source, setSource] = useState('');
  const sources = useQuery({
    queryKey: ['platform', 'imports', 'legacy-sources', session?.tenantId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/imports/legacy-sources')),
    staleTime: 5 * 60_000,
    enabled: !archive,
  });
  const converters = (sources.data?.items ?? []).filter((item) => item.types.includes(type));

  const upload = useMutation({
    mutationFn: (selected: File) => {
      const form = new FormData();
      form.append('type', type);
      form.append('file', selected, selected.name);
      if (source) {
        form.append('source', source);
      }
      return unwrap(
        tenantApi.POST('/api/v1/platform/imports', {
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onUploaded,
  });

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{t(archive ? 'plt:import.upload_step.archive_hint' : 'plt:import.upload_step.hint')}</Typography>
      {!archive && converters.length > 0 ? (
        <Stack spacing={1}>
          <TextField
            select
            label={t('plt:legacy.source')}
            value={source}
            onChange={(event) => setSource(event.target.value)}
            helperText={t('plt:legacy.source_hint')}
            sx={{ maxWidth: 420 }}
            inputProps={{ 'data-testid': 'import-legacy-source' }}
          >
            <MenuItem value="">{t('plt:legacy.source_none')}</MenuItem>
            {converters.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          {converters.find((item) => item.code === source)?.isSample ? (
            <Box>
              <SampleBadge />
            </Box>
          ) : null}
        </Stack>
      ) : null}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button component="label" htmlFor={inputId} variant="outlined" startIcon={<CloudUploadOutlinedIcon />} disabled={!write.canWrite}>
          {t('plt:import.upload_step.choose')}
          <input
            id={inputId}
            type="file"
            hidden
            accept={archive ? '.zip' : '.xlsx,.csv,.txt'}
            data-testid="import-file-input"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </Button>
        {file && (
          <Typography data-testid="import-file-name">
            {t('plt:import.upload_step.selected', { name: file.name })}
          </Typography>
        )}
      </Stack>
      {upload.error ? <ErrorState error={upload.error} compact /> : null}
      <Stack direction="row" spacing={1}>
        <Button onClick={onBack}>{t('common:actions.back')}</Button>
        <Button
          variant="contained"
          disabled={!file || upload.isPending || !write.canWrite}
          onClick={() => file && upload.mutate(file)}
          data-testid="import-upload"
        >
          {t('plt:import.upload_step.upload')}
        </Button>
      </Stack>
      {upload.isPending && <LinearProgress />}
    </Stack>
  );
}

function fieldTitle(t: (key: string) => string, field: ImportFieldView): string {
  return `${t(field.titleKey)}${field.required ? ' *' : ''}`;
}

function MappingStep({
  job,
  onBack,
  onValidating,
}: {
  job: ImportJobView;
  onBack: () => void;
  onValidating: (job: ImportJobView) => void;
}) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.import.run');
  const view = useQuery({
    queryKey: importKeys.mapping(session?.tenantId, job.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/imports/{id}/mapping', { params: { path: { id: job.id } } })),
  });

  if (view.isPending) {
    return <LoadingState rows={6} />;
  }
  if (view.error) {
    return <ErrorState error={view.error} onRetry={() => void view.refetch()} />;
  }
  return (
    <MappingForm
      key={view.dataUpdatedAt}
      view={view.data}
      canWrite={write.canWrite}
      onBack={onBack}
      onValidating={(updated) => {
        void queryClient.invalidateQueries({ queryKey: importKeys.mapping(session?.tenantId, job.id) });
        onValidating(updated);
      }}
    />
  );
}

function MappingForm({
  view,
  canWrite,
  onBack,
  onValidating,
}: {
  view: ImportMappingView;
  canWrite: boolean;
  onBack: () => void;
  onValidating: (job: ImportJobView) => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const initial = Object.keys(view.mapping).length > 0 ? view.mapping : view.suggested;
  const [mapping, setMapping] = useState<Record<string, string>>({ ...initial });
  const [moneyUnit, setMoneyUnit] = useState<ImportMoneyUnit>(view.moneyUnit);
  const [dateFormat, setDateFormat] = useState<ImportDateFormat>(view.dateFormat);
  const [profileId, setProfileId] = useState<string>(view.suggestedProfileId ?? '');
  const [profileName, setProfileName] = useState('');
  const moneyId = useId();
  const dateId = useId();
  const profileLabelId = useId();

  const fieldsByKey = useMemo(() => new Map(view.fields.map((field) => [field.key, field])), [view.fields]);
  const used = new Set(Object.values(mapping).filter(Boolean));
  const missing = view.fields.filter((field) => field.required && !used.has(field.key));
  const mappedFields = view.columns.map((column) => mapping[column]).filter((key): key is string => Boolean(key));

  const body = { mapping, moneyUnit, dateFormat, profileName: null };
  const preview = useQuery({
    queryKey: [...importKeys.mapping(session?.tenantId, view.jobId), 'preview', mapping, moneyUnit, dateFormat],
    queryFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/imports/{id}:preview', {
          params: { path: { id: view.jobId } },
          body,
        }),
      ),
    enabled: mappedFields.length > 0,
    placeholderData: (previous) => previous,
  });

  const applyProfile = (id: string) => {
    setProfileId(id);
    const profile = view.profiles.find((item) => item.id === id);
    if (profile) {
      setMapping(Object.fromEntries(Object.entries(profile.mapping).filter(([column]) => view.columns.includes(column))));
      setMoneyUnit(profile.moneyUnit);
      setDateFormat(profile.dateFormat);
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      await unwrap(
        tenantApi.PUT('/api/v1/platform/imports/{id}/mapping', {
          params: { path: { id: view.jobId } },
          body: { mapping, moneyUnit, dateFormat, profileName: profileName.trim() || null },
        }),
      );
      return unwrap(
        tenantApi.POST('/api/v1/platform/imports/{id}:validate', {
          params: { path: { id: view.jobId } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onValidating,
  });

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{t('plt:import.mapping_step.hint')}</Typography>
      {view.ignoredClinicalColumns.length > 0 && (
        <Alert severity="warning" data-testid="import-clinical-warning">
          {t('plt:import.mapping_step.clinical', { columns: view.ignoredClinicalColumns.join(t('plt:import.list_separator')) })}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {view.profiles.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id={profileLabelId}>{t('plt:import.mapping_step.profile')}</InputLabel>
            <Select
              labelId={profileLabelId}
              label={t('plt:import.mapping_step.profile')}
              value={profileId}
              onChange={(event) => applyProfile(event.target.value)}
              data-testid="import-profile-select"
            >
              <MenuItem value="">{t('plt:import.mapping_step.profile_none')}</MenuItem>
              {view.profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl>
          <FormLabel id={moneyId}>{t('plt:import.mapping_step.money_unit')}</FormLabel>
          <RadioGroup row aria-labelledby={moneyId} value={moneyUnit} onChange={(event) => setMoneyUnit(event.target.value as ImportMoneyUnit)}>
            <FormControlLabel value="Rial" control={<Radio />} label={t('plt:import.mapping_step.rial')} />
            <FormControlLabel value="Toman" control={<Radio />} label={t('plt:import.mapping_step.toman')} data-testid="import-money-toman" />
          </RadioGroup>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={dateId}>{t('plt:import.mapping_step.date_format')}</InputLabel>
          <Select
            labelId={dateId}
            label={t('plt:import.mapping_step.date_format')}
            value={dateFormat}
            onChange={(event) => setDateFormat(event.target.value as ImportDateFormat)}
          >
            {(['Auto', 'Jalali', 'Gregorian'] as const).map((format) => (
              <MenuItem key={format} value={format}>
                {t(`plt:import.mapping_step.date.${format}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('plt:import.steps.mapping')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('plt:import.mapping_step.file_column')}</TableCell>
              <TableCell>{t('plt:import.mapping_step.phoenix_field')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.columns.map((column, index) => (
              <TableRow key={column} data-testid="import-mapping-row" data-column={column}>
                <TableCell>
                  <bdi>{column}</bdi>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    fullWidth
                    value={mapping[column] ?? ''}
                    displayEmpty
                    inputProps={{ 'aria-label': column, 'data-testid': `import-field-${index}` }}
                    onChange={(event) => {
                      const value = event.target.value;
                      setMapping((current) => {
                        const next = Object.fromEntries(Object.entries(current).filter(([key, field]) => key !== column && field !== value));
                        return value ? { ...next, [column]: value } : next;
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('plt:import.mapping_step.ignore')}</em>
                    </MenuItem>
                    {view.fields.map((field) => (
                      <MenuItem key={field.key} value={field.key}>
                        {fieldTitle(t, field)}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {missing.length > 0 && (
        <Alert severity="error" data-testid="import-required-missing">
          {t('plt:import.mapping_step.required_missing', { fields: missing.map((field) => t(field.titleKey)).join(t('plt:import.list_separator')) })}
        </Alert>
      )}
      <Box>
        <Typography variant="h2" sx={{ mb: 1 }}>
          {t('plt:import.mapping_step.preview')}
        </Typography>
        {mappedFields.length === 0 ? (
          <Typography color="text.secondary">{t('plt:import.mapping_step.preview_empty')}</Typography>
        ) : preview.error ? (
          <ErrorState error={preview.error} compact />
        ) : !preview.data ? (
          <LoadingState rows={2} />
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader aria-label={t('plt:import.mapping_step.preview')} data-testid="import-preview">
              <TableHead>
                <TableRow>
                  <TableCell>{t('plt:import.mapping_step.preview_row')}</TableCell>
                  {mappedFields.map((key) => (
                    <TableCell key={key}>{fieldsByKey.get(key) ? t(fieldsByKey.get(key)!.titleKey) : key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.data.rows.map((row) => (
                  <TableRow key={String(row.rowNumber)}>
                    <TableCell>{faDigits(Number(row.rowNumber))}</TableCell>
                    {mappedFields.map((key) => (
                      <TableCell key={key}>
                        <bdi>{row.values[key] ?? ''}</bdi>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <TextField
        label={t('plt:import.mapping_step.save_profile')}
        value={profileName}
        onChange={(event) => setProfileName(event.target.value)}
        size="small"
        inputProps={{ maxLength: 100, 'data-testid': 'import-profile-name' }}
        sx={{ maxWidth: 360 }}
      />
      {save.error ? <ErrorState error={save.error} compact /> : null}
      <Stack direction="row" spacing={1}>
        <Button onClick={onBack}>{t('common:actions.back')}</Button>
        <Button
          variant="contained"
          disabled={missing.length > 0 || save.isPending || !canWrite}
          onClick={() => save.mutate()}
          data-testid="import-validate"
        >
          {t('plt:import.mapping_step.validate')}
        </Button>
      </Stack>
    </Stack>
  );
}

function CountCard({ labelKey, value, testId, tone }: { labelKey: string; value: number; testId: string; tone: 'success' | 'error' | 'warning' | 'info' }) {
  const { t } = useTranslation('plt');
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, borderColor: `${tone}.main` }} data-testid={testId}>
      <Typography variant="body2" color="text.secondary">
        {t(labelKey)}
      </Typography>
      <Typography variant="h2" component="p" color={`${tone}.main`} data-value={value}>
        {faDigits(value)}
      </Typography>
    </Paper>
  );
}

function useErrorRows(job: ImportJobView, enabled: boolean) {
  const { tenantApi, session } = useTenantSession();
  return useInfiniteQuery({
    queryKey: [...importKeys.errors(session?.tenantId, job.id), job.status],
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/platform/imports/{id}/errors', {
          params: { path: { id: job.id }, query: { cursor: pageParam ?? undefined, limit: 50 } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    enabled,
  });
}

export function ErrorsDownloadButton({ job }: { job: ImportJobView }) {
  const { t } = useTranslation('plt');
  const { tenantApi } = useTenantSession();
  const [error, setError] = useState<unknown>();
  const download = async () => {
    setError(undefined);
    const result = await tenantApi.GET('/api/v1/platform/imports/{id}/errors.xlsx', {
      params: { path: { id: job.id } },
      parseAs: 'blob',
    });
    if (result.response.ok && result.data) {
      saveBlob(result.data as Blob, `${job.fileName.replace(/\.[^.]+$/, '')}-errors.xlsx`);
    } else {
      setError(result.error ?? new Error('download'));
    }
  };
  return (
    <>
      <Button startIcon={<DownloadOutlinedIcon />} onClick={() => void download()} data-testid="import-errors-download">
        {t('plt:import.dry_run.download')}
      </Button>
      {error ? <ErrorState error={error} compact /> : null}
    </>
  );
}

function DryRunStep({ job, onEditMapping, onNext }: { job: ImportJobView; onEditMapping: () => void; onNext: () => void }) {
  const { t } = useTranslation('plt');
  const done = job.status === 'Validated' || job.status === 'ValidationFailed';
  const errors = useErrorRows(job, done);
  const rows = errors.data?.pages.flatMap((page) => page.items);

  const columns: DataTableColumn<ImportRowErrorView>[] = [
    { id: 'row', headerKey: 'plt:import.dry_run.columns.row', cell: (row) => faDigits(Number(row.rowNumber)), width: 90 },
    {
      id: 'field',
      headerKey: 'plt:import.dry_run.columns.field',
      cell: (row) => {
        const column = Object.entries(job.mapping).find(([, field]) => field === row.field)?.[0];
        return <bdi>{column ?? row.field ?? t('common:states.none')}</bdi>;
      },
    },
    { id: 'reason', headerKey: 'plt:import.dry_run.columns.reason', cell: (row) => t(`errors:${row.messageKey}`) },
  ];

  if (!done) {
    return (
      <Stack spacing={2} data-testid="import-validating">
        <Alert severity="info">{t('plt:import.dry_run.running')}</Alert>
        <LinearProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ImportStatusChip status={job.status} />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <CountCard labelKey="plt:import.dry_run.valid" value={Number(job.counts.valid)} testId="import-count-valid" tone="success" />
        <CountCard labelKey="plt:import.dry_run.invalid" value={Number(job.counts.invalid)} testId="import-count-invalid" tone="error" />
        <CountCard labelKey="plt:import.dry_run.duplicate_in_file" value={Number(job.counts.duplicateInFile)} testId="import-count-duplicate-in-file" tone="warning" />
        <CountCard labelKey="plt:import.dry_run.duplicate_existing" value={Number(job.counts.duplicateExisting)} testId="import-count-duplicate-existing" tone="info" />
      </Stack>
      {job.status === 'ValidationFailed' && <Alert severity="error">{t('plt:import.dry_run.failed')}</Alert>}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h2">{t('plt:import.dry_run.errors')}</Typography>
        {notValidCount(job) > 0 && <ErrorsDownloadButton job={job} />}
      </Stack>
      <DataTable
        captionKey="plt:import.dry_run.errors"
        columns={columns}
        rows={rows}
        getRowId={(row) => `${String(row.rowNumber)}-${row.field ?? ''}-${row.code}`}
        isLoading={errors.isPending}
        error={errors.error}
        onRetry={() => void errors.refetch()}
        hasMore={errors.hasNextPage}
        onLoadMore={() => void errors.fetchNextPage()}
        isFetchingMore={errors.isFetchingNextPage}
        emptyTitleKey="plt:import.dry_run.no_errors"
      />
      <Stack direction="row" spacing={1}>
        <Button onClick={onEditMapping} data-testid="import-edit-mapping">
          {t('plt:import.dry_run.edit_mapping')}
        </Button>
        <Button variant="contained" disabled={job.status !== 'Validated'} onClick={onNext} data-testid="import-dry-run-next">
          {t('plt:import.dry_run.next')}
        </Button>
      </Stack>
    </Stack>
  );
}

function ExecuteStep({
  job,
  onUpdated,
  onRestart,
  onShowHistory,
}: {
  job: ImportJobView;
  onUpdated: (job: ImportJobView) => void;
  onRestart: () => void;
  onShowHistory: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi } = useTenantSession();
  const write = useWriteAccess('plt.import.run');
  const idempotency = useIdempotencyKey();
  const skipping = notValidCount(job);
  const [skipInvalid, setSkipInvalid] = useState(false);

  const execute = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/imports/{id}:execute', {
          params: { path: { id: job.id } },
          body: { skipInvalid },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onUpdated,
  });

  const total = Number(job.totalRows);
  const percent = Number(job.progress.percent);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ImportStatusChip status={job.status} />
        <Typography color="text.secondary">
          <bdi>{job.fileName}</bdi>
        </Typography>
      </Stack>
      {job.status === 'Validated' && (
        <>
          <Typography>{t('plt:import.execute_step.summary', { valid: faDigits(Number(job.counts.valid)) })}</Typography>
          {skipping > 0 && (
            <>
              <FormControlLabel
                control={<Checkbox checked={skipInvalid} onChange={(event) => setSkipInvalid(event.target.checked)} />}
                label={t('plt:import.execute_step.skip_invalid', { count: faDigits(skipping) })}
                data-testid="import-skip-invalid"
              />
              {!skipInvalid && <Alert severity="warning">{t('plt:import.execute_step.skip_required')}</Alert>}
            </>
          )}
          {execute.error ? (
            <Stack spacing={1}>
              <ErrorState error={execute.error} compact />
              {isApiError(execute.error) && Array.isArray(execute.error.extensions.dependsOn) && (
                <Alert severity="info" data-testid="import-depends-on">
                  {t('plt:import.type_step.depends_on', {
                    types: (execute.error.extensions.dependsOn as string[]).map((type) => t(`plt:import.types.${type}`)).join(t('plt:import.list_separator')),
                  })}
                </Alert>
              )}
            </Stack>
          ) : null}
          <Box>
            <Button
              variant="contained"
              disabled={(skipping > 0 && !skipInvalid) || execute.isPending || !write.canWrite}
              onClick={() => execute.mutate()}
              data-testid="import-execute"
            >
              {t('plt:import.execute_step.start')}
            </Button>
          </Box>
        </>
      )}
      {job.status === 'Running' && (
        <Stack spacing={1} data-testid="import-running">
          <Typography>
            {t('plt:import.execute_step.running', {
              processed: faDigits(Number(job.progress.processed)),
              total: faDigits(total),
            })}
          </Typography>
          <LinearProgress variant="determinate" value={percent} aria-label={t('plt:import.execute_step.start')} />
        </Stack>
      )}
      {EXECUTED_STATUSES.includes(job.status) && (
        <Stack spacing={2} data-testid="import-result">
          {job.status === 'Failed' ? (
            <Alert severity="error">{t('plt:import.execute_step.failed')}</Alert>
          ) : (
            <Alert severity={job.status === 'RolledBack' ? 'info' : 'success'} data-testid="import-completed">
              {job.status === 'RolledBack'
                ? t('plt:import.rollback.done')
                : t('plt:import.execute_step.completed', {
                    imported: faDigits(Number(job.progress.imported)),
                    skipped: faDigits(Number(job.progress.skipped)),
                  })}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
            {Number(job.progress.skipped) > 0 && <ErrorsDownloadButton job={job} />}
            <RollbackAction job={job} onRolledBack={onUpdated} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onRestart} data-testid="import-new">
              {t('plt:import.execute_step.new')}
            </Button>
            <Button onClick={onShowHistory}>{t('plt:import.execute_step.history')}</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
